'use client';

import { useEffect } from 'react';
import useSWR from 'swr';
import {
  CalendarPlus,
  UserPlus,
  CheckCircle2,
  XCircle,
  CreditCard,
  Clock,
  Users,
  Activity,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/shared/ui/skeleton';
import { getAdminContextHeaders } from '@/lib/features/swr';
import type { ActivityFeedItem } from '@/lib/integrations/supabase/types';

interface ActivityFeedProps {
  businessId: string;
  refreshInterval?: number; // in milliseconds, default 60000 (1 minute)
  maxItems?: number;
}

const activityConfig: Record<
  string,
  {
    icon: typeof CalendarPlus;
    color: string;
    bgColor: string;
  }
> = {
  booking_created: {
    icon: CalendarPlus,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-500/10',
  },
  walk_in_added: {
    icon: UserPlus,
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-500/10',
  },
  checkout_completed: {
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-500/10',
  },
  no_show_marked: {
    icon: XCircle,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10',
  },
  barber_assigned: {
    icon: Users,
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-500/10',
  },
  payment_received: {
    icon: CreditCard,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-500/10',
  },
  queue_joined: {
    icon: Clock,
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-500/10',
  },
  queue_served: {
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-500/10',
  },
  queue_cancelled: {
    icon: XCircle,
    color: 'text-muted-foreground',
    bgColor: 'bg-muted/30',
  },
};

const defaultConfig = {
  icon: Activity,
  color: 'text-foreground',
  bgColor: 'bg-muted/30',
};

const fetcher = async (url: string) => {
  const res = await fetch(url, {
    headers: {
      ...getAdminContextHeaders(),
    },
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Failed to fetch');
  }
  return res.json();
};

export function ActivityFeed({
  businessId,
  refreshInterval = 60000,
  maxItems = 20,
}: ActivityFeedProps) {
  const { data, isLoading, error, mutate } = useSWR(
    businessId
      ? `/api/admin/activity-feed?businessId=${businessId}&limit=${maxItems}`
      : null,
    fetcher,
    {
      refreshInterval,
      revalidateOnFocus: false,
      dedupingInterval: 30000,
    }
  );

  const activities: ActivityFeedItem[] = data?.activities || [];

  if (isLoading) {
    return (
      <div className="card-bookdesk !p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-foreground" />
            <h3 className="text-base font-semibold text-foreground">Live Activity</h3>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-bookdesk !p-4 text-center text-sm text-muted-foreground">
        Failed to load activity feed
      </div>
    );
  }

  return (
    <div className="card-bookdesk !p-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-foreground" />
            <h3 className="text-base font-semibold text-foreground">Live Activity</h3>
          </div>
          <span className="text-xs text-muted-foreground">
            Auto-refresh {refreshInterval / 1000}s
          </span>
        </div>
      </div>

      {/* Activity List */}
      <div className="max-h-[320px] overflow-y-auto">
        {activities.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No recent activity
          </div>
        ) : (
          <div className="divide-y divide-border">
            {activities.map((activity) => {
              const config = activityConfig[activity.type] || defaultConfig;
              const Icon = config.icon;

              return (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 p-3 hover:bg-muted/30 transition-colors"
                >
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                      config.bgColor
                    )}
                  >
                    <Icon className={cn('h-4 w-4', config.color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-foreground truncate">
                        {activity.title}
                      </span>
                      <span className="text-[10px] text-muted-foreground flex-shrink-0">
                        {activity.relativeTime}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {activity.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
